import { replaceRealtimeSections } from './jobDetailsSlice';
// src/redux/slices/tbdApiSlice.js

import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../axios/axiosConfig';
import { portalApi } from '../api/portalApi';
import { setAllTbdForms, setTbdAuditItems } from './tbdSlice';
import { TBD_FINDER_PAUSED, TBD_REFRESH_TRIGGER_PAUSED } from '../../config/tbdFinder';

const pausedResult = getState => {
  const data = getState().tbdApi?.tbdList || [];
  const result = Promise.resolve({ data, paused: true });
  result.unwrap = () => Promise.resolve(data);
  return result;
};

// Debounce mechanism for the TBD refresh ping (kept as-is – this calls a
// separate URL not covered by the main REST API).
let debounceTimer = null;
let debounceWaiters = [];
const DEBOUNCE_DELAY = 3000; // 3 seconds debounce delay

const FORM_KEYS = [
  'generalProject',
  'generalStation',
  'soldToShipTo',
  'priceBreakdown',
  'ownersManual',
  'hcRequirements',
  'skidConstruction',
  'pumpManifoldPiping',
  'stationEnclosure',
  'dropPipeComments',
  'dropPipes',
  'levelControls',
  'fillSources',
  'pumps',
  'pumpDetails',
  'waterTreatment',
  'inletScreen',
  'inletStrainer',
  'dischargeStrainer',
  'dischargeFilter',
  'roSystem',
  'uvSystem',
  'fertigation',
  'skyHarvester',
  'shipLoose',
  'unregulatedPower',
  'unregulatedBreakers',
  'regulatedPower',
  'regulatedBreakers',
  'sequenceOfOperations',
  'electricalOptions',
  'telemetry',
  'telemetryAccessories',
  'selectedPumpOptionList',
  'customOptions',
  'warningReport',
  'changedOptions',
  'controlLogicOnly',
  'shipEarlyParent',
  'shipEarlyItems',
  'changeOrderParent',
  'changeOrderDetails',
];

const TABLE_TO_FORM_KEY = {
  generalprojectinfo: 'generalProject',
  projectinfo: 'generalProject',
  generalstationinfo: 'generalStation',
  stationinfo: 'generalStation',
  soldtoshipto: 'soldToShipTo',
  pricebreakdown: 'priceBreakdown',
  ownersmanual: 'ownersManual',
  ownersmanualrequirements: 'ownersManual',
  hcrequirements: 'hcRequirements',
  skidconstruction: 'skidConstruction',
  pumpmanifoldpiping: 'pumpManifoldPiping',
  pumpmanifold: 'pumpManifoldPiping',
  pumppiping: 'pumpManifoldPiping',
  stationenclosure: 'stationEnclosure',
  droppipecomments: 'dropPipeComments',
  droppipes: 'dropPipes',
  levelcontrols: 'levelControls',
  fillsources: 'fillSources',
  pumpparent: 'pumps',
  pumpdetails: 'pumpDetails',
  watertreatmenttoggles: 'waterTreatment',
  watertreatment: 'waterTreatment',
  inletscreen: 'inletScreen',
  inletfilter: 'inletScreen',
  inletstrainer: 'inletStrainer',
  dischargestrainer: 'dischargeStrainer',
  dischargefilter: 'dischargeFilter',
  rosystem: 'roSystem',
  ro: 'roSystem',
  uvsystem: 'uvSystem',
  uv: 'uvSystem',
  fertigation: 'fertigation',
  skyharvester: 'skyHarvester',
  shiploose: 'shipLoose',
  unregulatedpowersupply: 'unregulatedPower',
  unregulatedpower: 'unregulatedPower',
  unregulatedbreakers: 'unregulatedBreakers',
  regulatedpowersupply: 'regulatedPower',
  regulatedpower: 'regulatedPower',
  regulatedbreakers: 'regulatedBreakers',
  sequenceofoperations: 'sequenceOfOperations',
  electricaloptions: 'electricalOptions',
  telemetry: 'telemetry',
  telemetryaccessories: 'telemetryAccessories',
  selectedpumpoptionlist: 'selectedPumpOptionList',
  customoptions: 'customOptions',
  warningreport: 'warningReport',
  changedoptions: 'changedOptions',
  controllogiconly: 'controlLogicOnly',
  shipearlyparent: 'shipEarlyParent',
  shipearlyitems: 'shipEarlyItems',
  changeorderparent: 'changeOrderParent',
  changeorderdetails: 'changeOrderDetails',
};

const normalizeKey = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const hasTbdText = (value) => typeof value === 'string' && value.toLowerCase().includes('tbd');

const buildTbdFormsFromFinderRows = (rows) => {
  const forms = {};
  FORM_KEYS.forEach((formKey) => {
    forms[formKey] = { tbdFields: {}, hasTbd: false };
  });

  rows.forEach((item) => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const tableName = item.Table;
    const columnName = item.ColumnName;
    if (!tableName || !columnName || !hasTbdText(item.Value)) {
      return;
    }

    const formKey = TABLE_TO_FORM_KEY[normalizeKey(tableName)];
    if (!formKey) {
      return;
    }

    const current = forms[formKey] || { tbdFields: {}, hasTbd: false };
    const zoneKey = item.Zone !== undefined && item.Zone !== null ? String(item.Zone) : null;

    if (zoneKey) {
      const zoneFields = current.tbdFields[zoneKey] || {};
      zoneFields[columnName] = true;
      current.tbdFields[zoneKey] = zoneFields;
    } else {
      current.tbdFields[columnName] = true;
    }

    current.hasTbd = true;
    forms[formKey] = current;
  });

  return forms;
};

const hydrateTbdStateFromFinderRows = (dispatch, payload) => {
  const rows = Array.isArray(payload) ? payload.filter((item) => item && typeof item === 'object') : [];
  const tbdRows = rows.filter((item) => hasTbdText(item.Value));

  dispatch(setTbdAuditItems(tbdRows));
  dispatch(setAllTbdForms(buildTbdFormsFromFinderRows(tbdRows)));
};

/**
 * Backward-compatible wrapper: dispatch(fetchTbdData()) now initiates the RTK
 * Query for TBD data instead of running a standalone thunk.  Components that
 * already called dispatch(fetchTbdData()) will continue to work.
 */
export const fetchTbdData = (jobId) => (dispatch, getState) => {
  if (TBD_FINDER_PAUSED) return pausedResult(getState);
  const resultAction = dispatch(
    portalApi.endpoints.getTbdData.initiate(jobId ?? null, {
      forceRefetch: false,
    })
  );

  Promise.resolve(resultAction).then((result) => {
    if (result && !result.error && Object.prototype.hasOwnProperty.call(result, 'data')) {
      hydrateTbdStateFromFinderRows(dispatch, result.data);
    }
  });

  return resultAction;
};

/**
 * Backward-compatible wrapper: force-refetch the TBD data from the server.
 */
export const refreshTbdData = (jobId) => async (dispatch, getState) => {
  try {
    await pingTbdApiWithDebounce();
  } catch (error) {
    // Ping failures should not block UI writes; we'll still refetch TBD data.
    console.warn('TBD refresh ping failed before refetch:', error);
  }
  if (TBD_FINDER_PAUSED) return pausedResult(getState);

  const resultAction = dispatch(
    portalApi.endpoints.getTbdData.initiate(jobId ?? null, {
      forceRefetch: true,
    })
  );

  Promise.resolve(resultAction).then((result) => {
    if (result && !result.error && Object.prototype.hasOwnProperty.call(result, 'data')) {
      hydrateTbdStateFromFinderRows(dispatch, result.data);
    }
  });

  return resultAction;
};

// Function to ping TBD API with debouncing
const pingTbdApiWithDebounce = () => {
  if (TBD_REFRESH_TRIGGER_PAUSED || !process.env.REACT_APP_TBD_API_URL) return Promise.resolve(false);

  // Clear any existing timer to reset the debounce period
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  // Return a promise that resolves after the debounce period
  return new Promise((resolve) => {
    debounceWaiters.push(resolve);
    debounceTimer = setTimeout(async () => {
      debounceTimer = null;
      const waiters = debounceWaiters;
      debounceWaiters = [];
      const url = process.env.REACT_APP_TBD_API_URL;
      try {
        await axiosInstance.get(url, {
          headers: {
            'Access-Control-Allow-Origin': '*'
          }
        });
        // console.log("Ping to refresh the TBD table sent successfully.");
        waiters.forEach(done => done(true));
      } catch (pingError) {
        console.error("Failed to ping the TBD refresh URL:", pingError);
        waiters.forEach(done => done(false));
      }
    }, DEBOUNCE_DELAY);
  });
};

// Export the ping function for direct use in components
export const pingTbdApi = () => {
  return pingTbdApiWithDebounce();
};

// Create the slice
const tbdApiSlice = createSlice({
  name: 'tbdApi',
  initialState: {
    tbdList: [],
    loading: false,
    error: null,
  },
  reducers: {
    setTbdList: (state, action) => {
      state.tbdList = action.payload;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(replaceRealtimeSections, (state, action) => {
      if (!Array.isArray(action.payload.tbdAudit)) return;
      const jobId = action.payload.generalProject?.[0]?.ID;
      if (jobId == null) return;
      state.tbdList = [...state.tbdList.filter(row => String(row.ProjectID) !== String(jobId)), ...action.payload.tbdAudit];
    });
    builder
      // ── RTK Query: getTbdData ───────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getTbdData.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getTbdData.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.tbdList = action.payload;
        }
      )
      .addMatcher(
        portalApi.endpoints.getTbdData.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error?.message ?? 'Failed to fetch TBD data';
        }
      );
  },
});

export const { setTbdList } = tbdApiSlice.actions;
export default tbdApiSlice.reducer;
